import { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Images, Check, Loader2, ArrowRight, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { Shell, TopBar } from "../components/Shell";
import { api } from "../lib/api";

const SLOTS = [
  { key: "iopa", title: "IOPA / X-ray" },
  { key: "opg", title: "OPG / Panoramic" },
];

export default function AddXray() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sid = params.get("sid");
  const [photos, setPhotos] = useState({});
  const [busyKey, setBusyKey] = useState(null);
  const galleryRefs = useRef({});
  const cameraRefs = useRef({});

  const upload = async (key, file) => {
    setBusyKey(key);
    try {
      const fd = new FormData();
      fd.append("view_type", key);
      fd.append("file", file, `${key}.jpg`);
      await api.post(`/screenings/${sid}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => ({ ...p, [key]: { url: URL.createObjectURL(file) } }));
    } catch (e) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setBusyKey(null);
    }
  };

  const count = Object.keys(photos).length;

  return (
    <Shell header={<TopBar title="Add an X-ray or OPG?" onBack={() => navigate(`/mode?sid=${sid}`)} />}>
      <div className="px-6 py-5 flex flex-col gap-4 pb-28">
        <div className="rounded-2xl bg-accent/60 border border-border p-4 flex gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <ScanLine className="w-5 h-5 text-primary" />
          </div>
          <p className="text-[13px] text-foreground/80 leading-relaxed">
            Optional. If you have a recent <b>IOPA</b> or <b>OPG (panoramic X-ray)</b>, add it and we'll include an
            AI analysis in the <b>same report</b> as your teeth photos. Educational only — not a diagnosis.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {SLOTS.map((s) => {
            const done = photos[s.key];
            return (
              <div key={s.key} data-testid={`addxray-slot-${s.key}`}
                className="relative rounded-2xl border border-border bg-card overflow-hidden aspect-[4/3] flex flex-col">
                {done ? (
                  <>
                    <img src={done.url} alt={s.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></div>
                    <span className="absolute bottom-1.5 left-2 text-white text-[11px] font-semibold drop-shadow">{s.title}</span>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 p-2">
                    <span className="text-[12.5px] font-medium text-foreground text-center">{s.title}</span>
                    {busyKey === s.key ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
                      <div className="flex gap-2">
                        <button data-testid={`addxray-gallery-${s.key}`} onClick={() => galleryRefs.current[s.key]?.click()} className="tap w-9 h-9 rounded-full bg-accent flex items-center justify-center"><Images className="w-4 h-4 text-primary" /></button>
                        <button data-testid={`addxray-camera-${s.key}`} onClick={() => cameraRefs.current[s.key]?.click()} className="tap w-9 h-9 rounded-full bg-accent flex items-center justify-center"><Camera className="w-4 h-4 text-primary" /></button>
                      </div>
                    )}
                  </div>
                )}
                <input ref={(el) => (galleryRefs.current[s.key] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(s.key, f); }} />
                <input ref={(el) => (cameraRefs.current[s.key] = el)} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(s.key, f); }} />
              </div>
            );
          })}
        </div>

        {count > 0 && (
          <p data-testid="addxray-added-note" className="text-[12.5px] text-primary font-medium">
            {count} X-ray/OPG added — it will be analysed in your report.
          </p>
        )}
      </div>

      <div className="sticky bottom-0 px-6 py-4 bg-background border-t border-border flex flex-col gap-2">
        <button data-testid="addxray-generate" onClick={() => navigate(`/processing?sid=${sid}`)}
          className="tap w-full h-14 rounded-2xl brand-gradient text-white font-semibold text-base flex items-center justify-center gap-2">
          {count > 0 ? "Add & generate report" : "Generate my report"} <ArrowRight className="w-5 h-5" />
        </button>
        {count === 0 && (
          <button data-testid="addxray-skip" onClick={() => navigate(`/processing?sid=${sid}`)}
            className="tap w-full text-[13px] font-semibold text-muted-foreground py-1">
            Skip — I don't have an X-ray
          </button>
        )}
      </div>
    </Shell>
  );
}
